import * as React from 'react';
import { useLocation } from 'react-router-dom';
import {
  PWA_MOBILE_VIEWPORT_MQ,
  isPwaMobileNavEnabled,
} from 'utils/pwa-nav';

const PwaMobileNavContext = React.createContext<boolean>(false);

/**
 * Single listener for PWA mobile chrome (viewport, standalone, ?pwaNav=).
 * Wrap the app shell so child components can read mode without stale `useMemo`.
 */
export function PwaMobileNavProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const location = useLocation();
  const [enabled, setEnabled] = React.useState(() => isPwaMobileNavEnabled());

  const sync = React.useCallback(() => {
    setEnabled(isPwaMobileNavEnabled());
  }, []);

  React.useEffect(() => {
    sync();

    const media =
      typeof window.matchMedia === 'function'
        ? window.matchMedia(PWA_MOBILE_VIEWPORT_MQ)
        : null;
    const standaloneMedia =
      typeof window.matchMedia === 'function'
        ? window.matchMedia('(display-mode: standalone)')
        : null;

    media?.addEventListener('change', sync);
    standaloneMedia?.addEventListener('change', sync);
    window.addEventListener('resize', sync);
    window.addEventListener('orientationchange', sync);
    window.visualViewport?.addEventListener('resize', sync);

    return () => {
      media?.removeEventListener('change', sync);
      standaloneMedia?.removeEventListener('change', sync);
      window.removeEventListener('resize', sync);
      window.removeEventListener('orientationchange', sync);
      window.visualViewport?.removeEventListener('resize', sync);
    };
  }, [sync]);

  React.useEffect(() => {
    sync();
  }, [location.search, sync]);

  return (
    <PwaMobileNavContext.Provider value={enabled}>
      {children}
    </PwaMobileNavContext.Provider>
  );
}

/**
 * Reactive PWA mobile chrome flag (standalone, phone UA/viewport, or ?pwaNav=1).
 * Prefer this over `useMemo(() => isPwaMobileNavEnabled(), [])` so resize/rotation updates.
 */
export function usePwaMobileNavEnabled(): boolean {
  return React.useContext(PwaMobileNavContext);
}
